import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, DollarSign, Clock, Plus, TrendingUp, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

const KasirDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    today_bookings: 0,
    today_revenue: 0,
    unpaid_bookings: 0,
    cash_payments: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/kasir/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'kasir') {
        navigate('/kasir/login');
        return;
      }
    } catch (error) {
      navigate('/kasir/login');
      return;
    }
    
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      // Get all bookings
      const user = JSON.parse(localStorage.getItem('user'));
      await api.get(`/bookings/user/${user.id}`);
      const allBookingsResponse = await api.get('/admin/bookings');
      
      // Calculate today's stats
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayBookings = allBookingsResponse.data.filter(
        item => item.booking.booking_date === today
      );
      
      let todayRevenue = 0;
      let cashPayments = 0;
      let unpaidBookings = 0;
      
      todayBookings.forEach(item => {
        const numSlots = Array.isArray(item.booking.time_slots) ? item.booking.time_slots.length : 1;
        const amount = (item.venue?.price_per_hour || 0) * numSlots;
        const isPayable = !['cancelled', 'expired', 'expire'].includes(item.booking?.status);
        
        if (item.payment?.status === 'verified') {
          todayRevenue += amount;
          cashPayments++;
        } else if (isPayable) {
          unpaidBookings++;
        }
      });
      
      setStats({
        today_bookings: todayBookings.length,
        today_revenue: todayRevenue,
        unpaid_bookings: unpaidBookings,
        cash_payments: cashPayments
      });
      
      // Get recent bookings (limit 5)
      setRecentBookings(allBookingsResponse.data.slice(0, 5));
    } catch (error) {
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logout berhasil');
    navigate('/kasir/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-secondary rounded-md flex items-center justify-center">
                <span className="text-white font-black text-xl">K</span>
              </div>
              <span className="text-2xl font-heading font-black italic text-secondary uppercase">Kasir Panel</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="kasir-logout-btn"
              className="text-zinc-400 hover:text-red-500 font-medium transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div
        className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-10 2xl:px-12 py-8 lg:py-10"
        style={{ paddingBottom: 'calc(96px + env(safe-area-inset-bottom))' }}
      >
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white">Dashboard Kasir</h1>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/kasir/booking"
                data-testid="kasir-new-booking-btn"
                className="bg-secondary text-white hover:bg-secondary-hover rounded-none skew-btn px-6 py-3 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
              >
                <span>Buat Booking</span>
                <Plus className="w-5 h-5" />
              </Link>
              <Link
                to="/kasir/bookings"
                data-testid="kasir-view-bookings-btn"
                className="border-2 border-zinc-700 text-zinc-200 hover:border-secondary hover:text-secondary rounded-lg px-6 py-3 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
              >
                <span>Booking</span>
                <Calendar className="w-5 h-5" />
              </Link>
              <Link
                to="/kasir/whatsapp"
                className="border-2 border-zinc-700 text-zinc-200 hover:border-primary hover:text-primary rounded-lg px-6 py-3 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
              >
                <span>WhatsApp</span>
                <MessageCircle className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
              <div className="xl:col-span-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-zinc-900 border-l-4 border-secondary rounded-r-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                      <Calendar className="w-8 h-8 text-secondary" />
                      <span className="text-3xl font-black text-white">{stats.today_bookings}</span>
                    </div>
                    <div className="text-sm text-zinc-400 font-semibold uppercase">Booking Hari Ini</div>
                  </div>

                  <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="w-8 h-8 text-primary" />
                      <span className="text-2xl font-black text-white">{(stats.today_revenue / 1000).toFixed(0)}k</span>
                    </div>
                    <div className="text-sm text-zinc-400 font-semibold uppercase">Revenue Hari Ini</div>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate('/kasir/bookings?payment=unpaid')}
                    className="bg-zinc-900 border-l-4 border-secondary rounded-r-lg p-6 text-left hover:border-secondary/70 hover:bg-zinc-900/80 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Clock className="w-8 h-8 text-secondary" />
                      <span className="text-3xl font-black text-white">{stats.unpaid_bookings}</span>
                    </div>
                    <div className="text-sm text-zinc-400 font-semibold uppercase">Belum Lunas</div>
                  </button>

                  <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="w-8 h-8 text-primary" />
                      <span className="text-3xl font-black text-white">{stats.cash_payments}</span>
                    </div>
                    <div className="text-sm text-zinc-400 font-semibold uppercase">Bayar Tunai</div>
                  </div>
                </div>

                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <h2 className="text-2xl font-heading font-bold uppercase text-white">Booking Terbaru</h2>
                    <div className="text-sm text-zinc-500 font-semibold">
                      {format(new Date(), 'EEEE, dd MMM yyyy', { locale: idLocale })}
                    </div>
                  </div>
                  {recentBookings.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-zinc-500">Belum ada booking hari ini</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {recentBookings.map((item) => (
                        <div
                          key={item.booking.id}
                          className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3"
                        >
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-white truncate">{item.venue?.name}</h3>
                            <p className="text-sm text-zinc-400 truncate">
                              {item.booking.customer_name} | {item.booking.booking_date} |{' '}
                              {Array.isArray(item.booking.time_slots) ? item.booking.time_slots[0] : item.booking.time_slot}
                            </p>
                          </div>
                          <span
                            className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold ${
                              item.payment?.status === 'verified'
                                ? 'bg-primary/10 text-primary border border-primary/20'
                                : 'bg-red-500/10 text-red-300 border border-red-500/20'
                            }`}
                          >
                            {item.payment?.status === 'verified' ? 'Lunas' : 'Belum Lunas'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="xl:col-span-4 space-y-6">
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <h2 className="text-2xl font-heading font-bold uppercase text-white mb-4">Aksi Cepat</h2>
                  <div className="grid grid-cols-1 gap-4">
                    <Link
                      to="/kasir/booking"
                      className="bg-zinc-950 border border-zinc-800 rounded-lg p-6 hover:border-secondary/50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-secondary/10 border border-secondary/20 flex items-center justify-center shrink-0">
                          <Plus className="w-6 h-6 text-secondary group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-lg font-bold text-white uppercase font-heading">Buat Booking Baru</div>
                          <div className="text-sm text-zinc-400">Input booking untuk customer walk-in</div>
                        </div>
                      </div>
                    </Link>

                    <Link
                      to="/kasir/bookings"
                      className="bg-zinc-950 border border-zinc-800 rounded-lg p-6 hover:border-secondary/50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-secondary/10 border border-secondary/20 flex items-center justify-center shrink-0">
                          <Calendar className="w-6 h-6 text-secondary group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-lg font-bold text-white uppercase font-heading">Lihat Semua Booking</div>
                          <div className="text-sm text-zinc-400">Kelola dan verifikasi booking</div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default KasirDashboard;
